/**
 * Dashboard (SF-5095): a month's platform usage for staff. Customer figures only (Paid and Free
 * companies; Demo, Test, inactive and fake data are left out by sf-api), with the ServeFirst
 * Live company on its own line. Each section has its own request, so one failing shows its
 * error and the rest still load. SF-5117: a banner for the month in progress, like-for-like
 * comparisons, and the Changes section (companies that started or stopped giving feedback).
 * SF-5124: an even grid (two equal columns or full width, cards matching in height, one per row
 * below lg), the Active companies chart, and the empty-month message.
 */
import { useState } from 'react';
import { useCan } from '@refinedev/core';
import { Alert, Col, Row, Space, Typography } from 'antd';
import { TrendChart } from '../../components/charts/TrendChart';
import { MonthPicker } from '../../components/usage/MonthPicker';
import { MonthProgressBanner } from '../../components/usage/MonthProgressBanner';
import { UsageSection } from '../../components/usage/UsageSection';
import {
  useFeatureUsage,
  usePlatformChanges,
  usePlatformUsage,
  usePlatformUsageSeries,
  useRatingsMigration,
} from '../../components/usage/usageClient';
import {
  currentUsagePeriod,
  dropsCapNote,
  formatComputedAt,
} from '../../components/usage/usageNumbers';
import { EMPTY_MONTH_TEXT, isEmptyMonth, isInProgress } from '../../components/usage/usageProgress';
import { MONTHLY_TREND_MONTHS } from '../../utils/date';
import { formatNumber } from '../../utils/format';
import { ActiveCompaniesChart } from './components/ActiveCompaniesChart';
import { hasActiveSeries } from './components/activeSeries';
import { CHANGES_SECTION_ID, ChangesTables } from './components/ChangesTables';
import { BiggestDropsTable, TopCompaniesTable } from './components/CompanyTables';
import { FeatureUsageTable } from './components/FeatureUsageTable';
import { FeedbackLayers } from './components/FeedbackLayers';
import { PlatformTiles } from './components/PlatformTiles';
import { RatingsMigrationPanel } from './components/RatingsMigrationPanel';

const { Text, Title } = Typography;

export const DashboardPage: React.FC = () => {
  // The usage routes need admin:companies:read, the same as the companies list.
  const { data: canAccess, isLoading: canLoading } = useCan({
    resource: 'companies',
    action: 'list',
  });
  const enabled = canAccess?.can === true;

  // Only a month the user picked is stored; otherwise this month is worked out on each render,
  // so a tab left open over a month end moves on to the new month. This month is London's, as
  // the API counts it.
  const [pickedPeriod, setPickedPeriod] = useState<string | null>(null);
  const period = pickedPeriod ?? currentUsagePeriod();
  const onPeriodChange = (p: string) => setPickedPeriod(p === currentUsagePeriod() ? null : p);

  const platform = usePlatformUsage(period, { enabled });
  const series = usePlatformUsageSeries(MONTHLY_TREND_MONTHS, { enabled });
  const features = useFeatureUsage(period, { enabled });
  const ratings = useRatingsMigration(period, MONTHLY_TREND_MONTHS, { enabled });
  const changes = usePlatformChanges(period, { enabled });

  if (!canLoading && !enabled) {
    return (
      <div>
        <Title level={3}>Dashboard</Title>
        <Alert type="info" showIcon message="You need access to companies to see platform usage." />
      </div>
    );
  }

  const p = platform.data?.data;
  const platformState = {
    loading: canLoading || platform.isLoading,
    error: platform.isError,
    onRetry: () => platform.refetch(),
  };
  const updated = formatComputedAt(p?.computed_at);
  // An API without the changes route answers 404: leave the section out rather than show an error.
  // The data provider passes the AxiosError on, so the status is on its response.
  const changesError = changes.error as {
    response?: { status?: number };
    statusCode?: number;
  } | null;
  const changesMissing = (changesError?.response?.status ?? changesError?.statusCode) === 404;
  const showChanges = () =>
    document.getElementById(CHANGES_SECTION_ID)?.scrollIntoView?.({ behavior: 'smooth' });
  const lastMonthWords = isInProgress(p?.progress) ? 'the same days last month' : 'last month';
  const months = series.data?.data?.months;
  const seriesPoints = (months ?? []).map((m) => ({
    bucket: m.period,
    value: m.totals.total_feedback,
  }));
  const seriesState = {
    loading: canLoading || series.isLoading,
    error: series.isError,
    onRetry: () => series.refetch(),
  };
  // Shown while the series loads so the row does not jump; an older API sends no active figures,
  // and then the feedback chart takes the full row.
  const showActive = !series.isError && (seriesState.loading || hasActiveSeries(months));
  const emptyMonth = isEmptyMonth(p?.progress, p?.totals);
  const half = { xs: 24, lg: 12 };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" gutter={[16, 8]}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Dashboard
          </Title>
          {p && (
            <Text type="secondary">
              {updated ? `Last updated: ${updated}` : 'Not counted yet for this month'}
            </Text>
          )}
        </Col>
        <Col>
          <MonthPicker value={period} onChange={onPeriodChange} />
        </Col>
      </Row>

      <MonthProgressBanner progress={p?.progress} />

      <PlatformTiles
        data={p}
        {...platformState}
        onShowChanges={changesMissing ? undefined : showChanges}
      />

      <Row gutter={[16, 16]} align="stretch">
        <Col {...half}>
          <UsageSection
            title="Feedback by type"
            {...platformState}
            empty={emptyMonth}
            emptyText={EMPTY_MONTH_TEXT}
          >
            {p && <FeedbackLayers data={p} />}
          </UsageSection>
        </Col>
        <Col {...half}>
          <UsageSection
            title="Feature usage"
            loading={canLoading || features.isLoading}
            error={features.isError}
            onRetry={() => features.refetch()}
            empty={emptyMonth}
            emptyText={EMPTY_MONTH_TEXT}
          >
            {features.data?.data && <FeatureUsageTable data={features.data.data} />}
          </UsageSection>
        </Col>
      </Row>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={showActive ? 12 : 24}>
          <UsageSection
            title="Total feedback by month"
            subtitle={`Customers, last ${MONTHLY_TREND_MONTHS} months`}
            {...seriesState}
          >
            <TrendChart
              bare
              title="Total feedback by month"
              data={seriesPoints}
              granularity="monthly"
            />
          </UsageSection>
        </Col>
        {showActive && (
          <Col {...half}>
            <UsageSection
              title="Active companies"
              subtitle={`Customers by group, last ${MONTHLY_TREND_MONTHS} months`}
              {...seriesState}
            >
              {months && <ActiveCompaniesChart months={months} />}
            </UsageSection>
          </Col>
        )}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <UsageSection
            title="Old style ratings migration"
            loading={canLoading || ratings.isLoading}
            error={ratings.isError}
            onRetry={() => ratings.refetch()}
          >
            {ratings.data?.data && <RatingsMigrationPanel data={ratings.data.data} />}
          </UsageSection>
        </Col>
      </Row>

      <Row gutter={[16, 16]} align="stretch">
        <Col {...half}>
          <UsageSection
            title="Top 10 companies"
            {...platformState}
            empty={(p?.top_companies ?? []).length === 0}
            emptyText="No customer feedback this month"
          >
            {p && <TopCompaniesTable rows={p.top_companies} />}
          </UsageSection>
        </Col>
        <Col {...half}>
          <UsageSection
            title="Biggest drops"
            subtitle={
              p
                ? `Rule: down ${p.rule.drop_pct}% on ${lastMonthWords}, for companies with at least ${formatNumber(p.rule.min_previous)} last month${dropsCapNote(p.biggest_drops.length, p.biggest_drops_total)}`
                : undefined
            }
            {...platformState}
            empty={(p?.biggest_drops ?? []).length === 0}
            emptyText="No company dropped that far this month"
          >
            {p && <BiggestDropsTable rows={p.biggest_drops} />}
          </UsageSection>
        </Col>
      </Row>

      {!changesMissing && (
        <div id={CHANGES_SECTION_ID}>
          <UsageSection
            title="Changes"
            subtitle="Customers that started giving feedback this month, and those that stopped"
            loading={canLoading || changes.isLoading}
            error={changes.isError}
            onRetry={() => changes.refetch()}
          >
            {changes.data?.data && <ChangesTables data={changes.data.data} />}
          </UsageSection>
        </div>
      )}
    </Space>
  );
};
